import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Maximize2, PhoneOff, Compass, Volume2, Video, VideoOff } from 'lucide-react';

export default function LiveClassFloatingPill({
  remoteParticipantName = 'Participant',
  roleLabel = 'Student',
  elapsedSeconds = 0,
  isRemoteSpeaking = false,
  micEnabled = true,
  onToggleMic,
  remoteCameraTrack = null,
  isRemoteVideoMuted = false,
  isCoBrowsing = false,
  isPreparingCoBrowse = false,
  isTutorRole = false,
  onStartCoBrowsing,
  onStopCoBrowsing,
  onExpand,
  onEndClass,
  onLeave,
  isDark = true,
}) {
  const pillRef = useRef(null);
  const miniVideoRef = useRef(null);
  const [showVideoBubble, setShowVideoBubble] = useState(true);
  const [position, setPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      const w = window.innerWidth;
      return {
        x: Math.max(16, w - 280),
        y: 80, // Default to top-right to avoid obstructing bottom-bar navigation (Next button)
      };
    }
    return { x: 20, y: 80 };
  });

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const hasMovedRef = useRef(false);

  // Bounds clamping helper
  const clampPosition = useCallback((x, y) => {
    if (typeof window === 'undefined') return { x, y };
    const pillWidth = pillRef.current?.offsetWidth || 260;
    const pillHeight = pillRef.current?.offsetHeight || 60;
    const maxX = window.innerWidth - pillWidth - 12;
    const maxY = window.innerHeight - pillHeight - 16;
    return {
      x: Math.min(Math.max(12, x), Math.max(12, maxX)),
      y: Math.min(Math.max(16, y), Math.max(16, maxY)),
    };
  }, []);

  // Window resize listener to keep bubble within bounds
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPosition]);

  // Attach/detach remote camera track to mini video element
  useEffect(() => {
    if (miniVideoRef.current && remoteCameraTrack && showVideoBubble) {
      remoteCameraTrack.attach(miniVideoRef.current);
      return () => {
        remoteCameraTrack.detach(miniVideoRef.current);
      };
    }
  }, [remoteCameraTrack, showVideoBubble]);

  // Touch Drag Handling
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      initialX: position.x,
      initialY: position.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartRef.current.startX;
    const dy = touch.clientY - dragStartRef.current.startY;

    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      hasMovedRef.current = true;
      e.preventDefault(); // Prevent page scrolling during drag
      const newPos = clampPosition(
        dragStartRef.current.initialX + dx,
        dragStartRef.current.initialY + dy
      );
      setPosition(newPos);
    }
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
  };

  // Mouse Drag Handling
  const handleMouseDown = (e) => {
    // Only drag with left mouse button and not on interactive buttons
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const dx = moveEvent.clientX - dragStartRef.current.startX;
      const dy = moveEvent.clientY - dragStartRef.current.startY;

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        hasMovedRef.current = true;
        const newPos = clampPosition(
          dragStartRef.current.initialX + dx,
          dragStartRef.current.initialY + dy
        );
        setPosition(newPos);
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Format Elapsed Time (MM:SS or HH:MM:SS)
  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) {
      const remainingMins = mins % 60;
      return `${hrs}:${remainingMins < 10 ? '0' : ''}${remainingMins}:${secs < 10 ? '0' : ''}${secs}`;
    }
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Initials
  const initial = (remoteParticipantName || 'P').trim().charAt(0).toUpperCase();

  return (
    <div
      ref={pillRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none',
      }}
      className="z-[99999] select-none flex flex-col items-end pointer-events-auto"
    >
      {/* Attached Collapsible Mini Video Preview */}
      {showVideoBubble && remoteCameraTrack && (
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          className="mb-1.5 cursor-grab active:cursor-grabbing relative aspect-video w-36 sm:w-44 rounded-xl overflow-hidden border shadow-2xl bg-black border-slate-700/80 animate-in fade-in zoom-in-95 duration-150"
        >
          <video
            ref={miniVideoRef}
            autoPlay
            playsInline
            className={`w-full h-full object-cover ${isRemoteVideoMuted ? 'hidden' : 'block'}`}
          />
          {isRemoteVideoMuted && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-1 bg-slate-900/90 text-white">
              <VideoOff className="w-4 h-4 text-rose-400 mb-0.5" />
              <span className="text-[9px] text-slate-300">Camera Off</span>
            </div>
          )}
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[8px] font-bold bg-slate-950/80 text-white rounded">
            {remoteParticipantName}
          </div>
        </div>
      )}

      {/* Main Pill Bar */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        className={`cursor-grab active:cursor-grabbing flex items-center gap-2 sm:gap-2.5 px-3 py-2 rounded-2xl shadow-2xl backdrop-blur-xl border transition-shadow duration-200 ${
          isDark
            ? 'bg-slate-900/90 border-slate-700/80 text-white shadow-black/60'
            : 'bg-white/95 border-slate-200/90 text-slate-800 shadow-slate-900/20'
        }`}
      >
        {/* Remote Participant Avatar with Active Speaker Pulse */}
        <div className="relative shrink-0">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm text-white shadow-inner ${
              isRemoteSpeaking
                ? 'bg-gradient-to-br from-emerald-500 to-teal-600 ring-2 ring-emerald-400 ring-offset-2 ring-offset-slate-900 animate-pulse'
                : 'bg-gradient-to-br from-emerald-600 to-teal-600'
            }`}
          >
            {initial}
          </div>
          {isRemoteSpeaking && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
              <Volume2 className="w-1.5 h-1.5 text-white" />
            </span>
          )}
        </div>

        {/* Info: Name, Role, Timer, & Screen Share Badge */}
        <div
          className="flex flex-col min-w-[70px] max-w-[110px] sm:max-w-[130px] overflow-hidden"
          onClick={() => {
            if (!hasMovedRef.current && onExpand) {
              onExpand();
            }
          }}
        >
          <div className="flex items-center gap-1">
            <span className="text-xs font-bold truncate leading-tight">
              {remoteParticipantName}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] font-mono font-medium text-emerald-500 dark:text-emerald-400">
              {formatTimer(elapsedSeconds)}
            </span>
            {isTutorRole && isPreparingCoBrowse && (
              <span className="px-1.5 py-0.2 text-[9px] font-semibold rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1 shrink-0 border border-emerald-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                <span>Prep</span>
              </span>
            )}
            {isTutorRole && isCoBrowsing && (
              <span className="px-1.5 py-0.2 text-[9px] font-semibold rounded bg-teal-500/20 text-teal-300 flex items-center gap-1 shrink-0 border border-teal-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-ping" />
                <span>Live</span>
              </span>
            )}
          </div>
        </div>

        {/* Vertical Divider */}
        <div className={`h-6 w-px mx-0.5 ${isDark ? 'bg-slate-700/80' : 'bg-slate-200'}`} />

        {/* Quick Action Controls */}
        <div className="flex items-center gap-1 shrink-0" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          {/* Toggle Video Bubble (if video track exists) */}
          {remoteCameraTrack && (
            <button
              type="button"
              onClick={() => setShowVideoBubble(!showVideoBubble)}
              className={`p-1.5 rounded-lg border transition ${
                showVideoBubble
                  ? 'bg-emerald-600/30 border-emerald-500/40 text-emerald-400 hover:bg-emerald-600/40'
                  : isDark
                    ? 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'
                    : 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200'
              }`}
              title={showVideoBubble ? 'Hide Video Preview' : 'Show Video Preview'}
            >
              {showVideoBubble ? <Video className="w-3.5 h-3.5" /> : <VideoOff className="w-3.5 h-3.5" />}
            </button>
          )}

          {/* Start Co-Browsing Icon-Only Button */}
          {isTutorRole && isPreparingCoBrowse && onStartCoBrowsing && (
            <button
              type="button"
              onClick={onStartCoBrowsing}
              className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow transition flex items-center justify-center animate-pulse"
              title="Start Co-Browsing with Student"
            >
              <Compass className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Stop Co-Browsing Icon-Only Button */}
          {isTutorRole && isCoBrowsing && onStopCoBrowsing && (
            <button
              type="button"
              onClick={onStopCoBrowsing}
              className="p-1.5 rounded-lg bg-teal-600 hover:bg-rose-600 text-white shadow transition flex items-center justify-center"
              title="Stop Co-Browsing"
            >
              <Compass className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Mic Toggle */}
          <button
            type="button"
            onClick={onToggleMic}
            className={`p-1.5 rounded-lg border transition ${
              micEnabled
                ? isDark
                  ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700'
                  : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                : 'bg-rose-500/20 border-rose-500/40 text-rose-400 hover:bg-rose-500/30'
            }`}
            title={micEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
          >
            {micEnabled ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
          </button>

          {/* Expand Back to Full Classroom */}
          <button
            type="button"
            onClick={onExpand}
            className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white shadow transition flex items-center justify-center"
            title="Expand to Full Classroom"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>

          {/* End Class */}
          <button
            type="button"
            onClick={onEndClass || onLeave}
            className="p-1.5 rounded-lg bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/30 text-rose-400 hover:text-rose-300 transition flex items-center justify-center"
            title="End Class"
          >
            <PhoneOff className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
